import { Bell, CheckCircle, Calendar, MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface Notification {
  id: string;
  type: string;
  icon: any;
  iconColor: string;
  iconBg: string;
  title: string;
  message: string;
  time: string;
  unread: boolean;
}

interface NotificationModalProps {
  showNotifications: boolean;
  setShowNotifications: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
  showNotifications,
  setShowNotifications,
}) => {
  const notificationRef = useRef<HTMLDivElement>(null);

  // Close when clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      ) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setShowNotifications]);

  // Dummy notifications data
  const notifications: Notification[] = [
    {
      id: '1',
      type: 'task_completed',
      icon: CheckCircle,
      iconColor: 'text-green-500',
      iconBg: 'bg-green-100',
      title: 'Task "GST Filing – Goyal Traders"',
      message: 'marked as Completed by Jay Soni.',
      time: '5 mins ago',
      unread: true,
    },
    {
      id: '2',
      type: 'client_updated',
      icon: CheckCircle,
      iconColor: 'text-green-500',
      iconBg: 'bg-green-100',
      title: 'Client "Zenith Pharma" updated with',
      message: 'new GST number.',
      time: '12 mins ago',
      unread: true,
    },
    {
      id: '3',
      type: 'task_due',
      icon: Calendar,
      iconColor: 'text-red-500',
      iconBg: 'bg-red-100',
      title: 'Task "ROC Compliance – Rajat',
      message: 'Constructions" is due tomorrow.',
      time: '30 mins ago',
      unread: false,
    },
    {
      id: '4',
      type: 'comment',
      icon: MessageCircle,
      iconColor: 'text-blue-500',
      iconBg: 'bg-blue-100',
      title: 'Comment added by Sahil Vhora',
      message: 'on task "Books Finalization – Goyal Traders".',
      time: 'Today at 10:45 AM',
      unread: false,
    },
  ];

  const unreadCount = notifications.filter((n) => n.unread).length;

  return (
    <div className="relative" ref={notificationRef}>
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className="flex items-center space-x-2 hover:bg-[#455078] px-2 sm:px-3 py-1.5 rounded transition-colors relative"
      >
       <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M13.5999 10.8C12.7162 10.8 11.9999 10.0837 11.9999 9.20002V6.40002C11.9999 4.1909 10.209 2.40002 7.9999 2.40002C5.79078 2.40002 3.9999 4.1909 3.9999 6.40002V9.20002C3.9999 10.0837 3.28358 10.8 2.3999 10.8H13.5999Z" fill="white" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M6.04004 12.8C6.2254 13.7129 7.03212 14.4 7.99972 14.4C8.96732 14.4 9.77404 13.7129 9.9594 12.8H6.04004Z" fill="white"/>
</svg>

        
        <span className="text-sm hidden sm:inline">Notifications</span>
        {unreadCount > 0 && (
          <span className="  bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showNotifications && (
        <div className="absolute top-full right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                  {unreadCount}
                </span>
              )}
            </div>
            <button
              className="text-blue-600 text-sm hover:text-blue-800 font-medium"
              onClick={() => {
                // Mark all read logic here
                console.log('Mark all as read');
              }}
            >
              Mark all as read
            </button>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((notification) => {
                const IconComponent = notification.icon;
                return (
                  <div
                    key={notification.id}
                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                      notification.unread ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => {
                      console.log('Notification clicked:', notification.id);
                    }}
                  >
                    <div className="flex space-x-3">
                      <div
                        className={`w-8 h-8 ${notification.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}
                      >
                        <IconComponent
                          size={16}
                          className={notification.iconColor}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">
                                {notification.title}
                              </span>
                              <span className="ml-1">{notification.message}</span>
                            </p>
                          </div>
                          <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                            {notification.time}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center">
                <Bell size={48} className="text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  You're all caught up!
                </h4>
                <p className="text-sm text-gray-500">
                  No new notifications right now.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
